import { KeyboardEvent, useRef, useEffect, useMemo, useLayoutEffect, useState } from "react";
import { titleMRClass } from "src/components/common/common";
import { XButtonForm } from "../XButtonForm";
import { handleKeyDown } from "src/utils/commonForm/commonFormUtils";

interface Props {
	title?: string;
	value: string | number;
	editable: boolean;
	titleless?: boolean;
	name?: string;
	//onChange: 사용자의 입력을 실시간으로 반영하여 화면에 보여주기 위한 임시 문자열 상태를 업데이트
	//자연수, 문자는 onchange 하나만으로 처리 가능
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	// onBlur: 사용자가 입력을 마치고 다른 곳을 클릭하면(포커스 아웃),
	// 입력된 문자열을 최종적으로 올바른 number 타입으로 변환하여 데이터의 정합성을 맞춥
	// 소수점 처리에 이용됨
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	wrapperStyle?: {
		wrapperStyle: string;
		titleStyle: string;
		inputStyle: string;
	};
	type?: string;
	maxLength?: number;
	onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
	onLoadFocus?: boolean;
	placeholder?: string;
}

export const InputForm = (props: Props) => {
	const {
		title,
		value,
		editable,
		titleless = false,
		name,
		onChange,
		onBlur,
		wrapperStyle = {
			wrapperStyle: "w-[300px] min-w-[300px] mr-[10px] mb-[5px]",
			titleStyle: "w-[100px] min-w-[100px] text-left",
			inputStyle: "w-[200px] min-w-[200px]",
		},
		type = 'text',
		maxLength = 100,
		onLoadFocus = false,
		placeholder = '',
	} = props;

	const inputRef = useRef<HTMLInputElement>(null);
	const cursorStateRef = useRef<{ digits: number, isTriggered: boolean }>({ digits: 0, isTriggered: false });

	// 컴포넌트가 처음 생성될 때의 value 타입을 기준으로 필드 종류를 확정
	const [isNumericField] = useState(typeof value === 'number');

	const displayValue = useMemo(() => {
		const stringValue = String(value ?? '');
		// 숫자 필드이고, 값이 비어있지 않으며, 문자열이 '.'으로 끝나지 않을 때만 서식을 적용
		if (isNumericField && stringValue.length > 0 && !stringValue.endsWith('.')) {
			// toLocaleString()이 null/undefined에서 에러를 일으킬 수 있으므로 Number()로 한 번 더 감싸줍니다.
			return Number(value).toLocaleString('en-US');
		}
		return stringValue;
	}, [value, isNumericField]);

	useEffect(() => {
		if (onLoadFocus) {
			inputRef.current?.focus();
		}
	}, [onLoadFocus]);
	
	useLayoutEffect(() => {
		if (isNumericField && inputRef.current && cursorStateRef.current.isTriggered) {
			const { digits } = cursorStateRef.current;
			const newFormattedValue = inputRef.current.value;
			
			let newCursorPos = 0;
			let digitCount = 0;
			for (let i = 0; i < newFormattedValue.length; i++) {
				if (digitCount === digits) break;
				newCursorPos++;
				const char = newFormattedValue[i];
				if (char >= '0' && char <= '9') {
					digitCount++;
				}
			}
			
			inputRef.current.setSelectionRange(newCursorPos, newCursorPos);
			cursorStateRef.current.isTriggered = false;
		}
	});

	const onKeyDown = props.onKeyDown != null ?
		props.onKeyDown :
		(e: KeyboardEvent<HTMLInputElement>) => {
			if (e.shiftKey && e.key === "Tab") { e.preventDefault(); handleKeyDown(e, 'focus-1');
			} else if (e.key === "Tab") { e.preventDefault(); handleKeyDown(e, 'focus+1');
			} else { handleKeyDown(e, 'normal'); }
		}

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (!onChange) return;
		const { value: inputValue, selectionStart } = e.target;
		
		if (isNumericField) {
			const newUnformatted = inputValue.replace(/,/g, '');

			// 숫자 필드에는 숫자와 소수점만 입력되도록 강제 (소수점은 한 번만 허용)
			if (!/^-?\d*\.?\d*$/.test(newUnformatted)) {
				if(inputRef.current) inputRef.current.value = displayValue;
				return;
			}
			
			const digitsBeforeCursor = (inputValue.substring(0, selectionStart ?? 0).replace(/,/g, '')).length;
			cursorStateRef.current = { digits: digitsBeforeCursor, isTriggered: true };
			
			const syntheticEvent = {
				...e,
				target: { ...e.target, value: newUnformatted, name: name || '', id: name || '' }
			} as React.ChangeEvent<HTMLInputElement>;
			onChange(syntheticEvent);
		} else {
			// 일반 텍스트 필드는 모든 입력을 그대로 전달
			onChange(e);
		}
	};

	return (
		<div className={`flex h-[20px] ${wrapperStyle.wrapperStyle}`}>
			{title &&
				<>
					<p className={`${wrapperStyle.titleStyle} form-title ${titleMRClass} cursor-pointer text-[16px] ${titleless ? 'hidden' : ''}`}
						onClick={() => inputRef.current?.focus()} 
						onKeyDown={onKeyDown}
						tabIndex={ editable ? 0 : -1}
						>
						{title}
					</p>
					<div className={`relative ${wrapperStyle.inputStyle}`}>
						<input
							className={`border-[1px] rounded-[4px] px-[2px] w-full text-[16px]
								${editable ? "bg-white" : "bg-gray-disable"}
								${isNumericField ? 'text-right' : ''}`
							}
							ref={inputRef}
							value={displayValue}
							disabled={!editable}
							name={name || ""}
							type={type}
							maxLength={maxLength}
							onChange={handleInputChange}
							onBlur={onBlur}
							onKeyDown={onKeyDown}
							placeholder={placeholder}
						/>
						{!isNumericField && editable &&
							<XButtonForm
								onClick={() => {
									if (inputRef.current && name && onChange) {
										const event = { target: { name, value: '' } } as React.ChangeEvent<HTMLInputElement>;
										onChange(event);
									}
								}}
							/>
						}
					</div>
				</>
			}
		</div>
	)
}